import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ISSUE_LABEL, STATUS_LABEL } from "../lib/api";
import { devLog } from "../lib/log";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Loader2, CheckCircle2, Clock, MapPin, Car, Receipt, FileText, Download } from "lucide-react";

const STAGES = ["pending", "deposit_paid", "in_progress", "completed", "fully_paid"];

const invoicePayLabel = (type) => {
  if (type === "deposit") return "Reserva";
  if (type === "full") return "Total";
  return "Final";
};

export default function Tracking() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/service-requests/${id}`);
        setData(res.data);
      } catch (e) {
        devLog.error("Error loading service request:", e);
        setErr("No se encontró la solicitud");
      }
      try {
        const inv = await api.get(`/service-requests/${id}/invoices`);
        setInvoices(inv.data || []);
      } catch (e) {
        devLog.warn("Error loading invoices (opcional):", e);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [id]);

  const downloadInvoice = async (number) => {
    try {
      const res = await api.get(`/invoices/${id}/${number}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      devLog.error("Error downloading invoice:", e);
    }
  };

  if (err) return (
    <div className="min-h-screen bg-[#0A0A0A]"><Header />
      <main className="max-w-xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-bold text-3xl">{err}</h1>
      </main><Footer />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FFC107]" />
    </div>
  );

  const currentStage = STAGES.indexOf(data.status);
  const remaining = (data.estimated_price - (data.deposit_amount || 0)).toFixed(2);
  const isPartial = data.deposit_paid && !data.final_paid && data.payment_option === "deposit";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="max-w-3xl mx-auto px-5 md:px-8 py-14">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FFC107] mb-3">Seguimiento · #{id.slice(0,8)}</div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight mb-2">{ISSUE_LABEL[data.issue_type]}</h1>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FFC107]/30 bg-[#FFC107]/5 rounded-full text-xs font-mono text-[#FFC107] mt-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFC107] blink-dot" />
          {STATUS_LABEL[data.status] || data.status}
        </div>

        {/* Timeline */}
        <div className="sc-card rounded-md p-6 mt-8">
          <h3 className="font-display font-semibold text-lg mb-5">Estado del servicio</h3>
          <div className="space-y-4">
            {[
              { k: "pending", t: "Solicitud creada", i: Receipt },
              { k: "deposit_paid", t: "Pago recibido", i: CheckCircle2 },
              { k: "in_progress", t: "Cerrajero en camino", i: Clock },
              { k: "completed", t: "Trabajo finalizado", i: CheckCircle2 },
            ].map((s, i) => {
              const done = currentStage >= STAGES.indexOf(s.k);
              return (
                <div key={s.k} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done ? "bg-[#FFC107] text-black" : "bg-white/5 text-[#71717A]"}`}>
                    <s.i className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className={`font-medium ${done ? "text-white" : "text-[#71717A]"}`}>{s.t}</div>
                  </div>
                  {done && <div className="text-xs font-mono text-[#10B981]">OK</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Details */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="sc-card rounded-md p-5">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#71717A] mb-3"><Car className="w-3.5 h-3.5" /> Vehículo</div>
            <div className="font-display font-semibold text-lg">{data.vehicle_brand} {data.vehicle_model}</div>
            <div className="text-sm text-[#A1A1AA]">Año {data.vehicle_year}</div>
          </div>
          <div className="sc-card rounded-md p-5">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#71717A] mb-3"><MapPin className="w-3.5 h-3.5" /> Ubicación</div>
            <div className="text-sm">{data.location?.address}</div>
            {data.location?.lat && <div className="text-xs text-[#71717A] mt-1">GPS: {data.location.lat.toFixed(4)}, {data.location.lng.toFixed(4)}</div>}
          </div>
        </div>

        {/* Payment */}
        <div className="sc-card rounded-md p-6 mt-4">
          <h3 className="font-display font-semibold text-lg mb-4">Resumen económico</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#A1A1AA]">Total</span><span className="font-mono">{data.estimated_price.toFixed(2)}€</span></div>
            <div className="flex justify-between"><span className="text-[#A1A1AA]">Depósito pagado</span><span className="font-mono text-[#10B981]">{data.deposit_paid ? `${data.deposit_amount.toFixed(2)}€` : "—"}</span></div>
            {isPartial && (<div className="flex justify-between pt-2 border-t border-white/5 font-medium"><span>Pendiente al finalizar</span><span className="font-mono text-[#FFC107]">{remaining}€</span></div>)}
            {data.final_paid && (<div className="flex justify-between pt-2 border-t border-white/5"><span className="text-[#10B981]">Pago completo</span><span className="font-mono text-[#10B981]">✓</span></div>)}
          </div>
        </div>

        {/* Facturas (si hay) */}
        {invoices.length > 0 && (
          <div className="sc-card rounded-md p-6 mt-4" data-testid="tracking-invoices">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-[#FFC107]" />
              <h3 className="font-display font-semibold text-lg">Tus facturas</h3>
            </div>
            <div className="space-y-2">
              {invoices.map((inv) => (
                <button
                  key={inv.number}
                  onClick={() => downloadInvoice(inv.number)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-white/5 hover:bg-[#FFC107]/10 border border-white/10 hover:border-[#FFC107]/30 transition-colors group"
                  data-testid={`tracking-download-${inv.number}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-[#FFC107] flex-shrink-0" />
                    <div className="min-w-0 text-left">
                      <div className="text-sm font-mono text-white truncate">Factura {inv.number}</div>
                      <div className="text-[10px] text-[#71717A] uppercase tracking-wider">
                        {new Date(inv.issued_at).toLocaleDateString("es-ES")} · {invoicePayLabel(inv.payment_type)} · {Number(inv.total).toFixed(2)}€
                      </div>
                    </div>
                  </div>
                  <Download className="w-4 h-4 text-[#71717A] group-hover:text-[#FFC107] flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
